import React, {useEffect} from 'react';
import Config from "../../../../config";
import {Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Paper, Typography} from "@mui/material";
import {AddCircleOutline, HighlightOff, Settings, Sync} from "@mui/icons-material";
import CatalogResults from "./components/catalog_results";
import EnhancedTable from "../../../../components/table";
import { styled } from '@mui/material/styles';
import { pageContentGap, DialogHeader, Header } from '../../../../theme';
import axios from "axios";
import {ApprovalStatusColumn, CategoryColumn, CreatedDateColumn, EventIdColumn, LastUpdateColumn, ListingStatusColumn, MerchantNameColumn, MessageIdColumn, MessageSkuColumn, MessageStatusColumn, ParentSkuColumn, ProductColumn, SupplierColumn, TagsColumn} from "./components/catalog_columns";
import Helpers, {getCompanyNames} from "../../../../helpers";
import {ExportIcon, ImportIcon} from "../../../../utils/icons";
import {getAllTags, SUPPLIER_SYNDICATION_ENABLED, utilGetPartners, utilSearchInventory} from "../../../../utils/utils";
import CatalogTab from "./components/catalog_tab";
import CatalogEventResults from "./components/catalog_event_results";
import moment from "moment";
import Auth from "../../../../auth";
import CatalogCheckbox from "../../../../components/EnhancedCheckbox";
import EnhancedSelect from "../../../../components/EnhancedSelect";
import RepliedIcon from "@mui/icons-material/Lens";
import Moment from "react-moment";
import MessageResults from "./components/message_results";
import NewMessage from "../../components/Dialogs/NewMessage";
import RefreshSupplierDataModal from "../../components/Dialogs/RefreshSupplierDataModal";
import Dashboard from "./containers/dashboard/dashboard";
import {Link} from "react-router-dom";
import ContentLoader from "../../../../components/content_loader";
import { TagsHoverTemplate } from "./components/catalog_columns";

// region TypeScript Interfaces
interface CatalogProps {
    isRetailer: boolean;
    retailers?: any[];
    history: any;
    location: any;
    setTitle?: (title: string, isPoc?: boolean) => void;
}

interface CatalogConfigShape {
    odata?: boolean;
    selectable?: boolean;
    order?: string;
    orderBy?: string;
    pageSize?: number;
    pageSizes?: number[];
    title?: string;
    refresh?: boolean;
    term?: string;
    facets?: string[];
    filter?: any;
    filterInUrl?: boolean;
    savedSearchEnabled?: boolean;
    tableType?: string;
    url?: string;
    onFormatData?: (data: any[]) => Promise<any[]>;
    columns: any[];
    keyField?: string;
}

interface SkuOption {
    label: string;
    value: string | number;
}
// endregion

export const retailerCatalogSearch = Config.api + "/odata/Company/Functions.RetailerCatalogSearch";
export const supplierCatalogSearch = Config.api + "/odata/Company/Functions.SupplierCatalogSearch";

const Root = styled('div')(({ theme }: any) => ({
    // maxWidth: "calc(100vw - 220px - 42px)"
}));


//region Config Column getData functions
const getCategories = async (): Promise<SkuOption[]> => {
    try {
        const response = await axios.get(Config.api + "/api/v2/ProductOnboardingCenter/Categories");
        return response.data.Records.map((record: any) => ({label: record.Name, value: record.Name}));
    }
    catch (error) {
        console.error("Error getting categories: ", error);
        return []
    }
}

const getAttributeSets = async (): Promise<SkuOption[]> => {
    try {
        const response = await axios.get(Config.api + "/api/v2/ProductOnboardingCenter/AttributeSets");
        return response.data.Records.map((record: any) => ({label: record.Name, value: record.Name}));
    } catch (error) {
        console.error("Error getting attribute sets: ", error);
        return []
    }
}

const getTags = async (isRetailer: boolean): Promise<SkuOption[]> => {
    try {
        const isPartner = new Helpers().getUrlObject("catalog") === "partnercatalog";
        const runStatus = await getAllTags(isPartner, isRetailer);

        if (runStatus.errorMessage) return [];
        else return runStatus.map((tag: string) => ({label: tag, value: tag}));
    }
    catch (error) {
        console.error("Error getting tags: ", error);
        return []
    }
}

const getInventory = async(productData: any[], isRetailer: boolean): Promise<any[]> => {
    const copiedData = [...productData];
    if(!copiedData) return copiedData;

    const allSkus = copiedData.map((product) => product.sku).filter((sku) => sku !== null && sku !== undefined && sku !== "");
    if(allSkus.length === 0) return copiedData;
    const quantity = await utilSearchInventory(allSkus, isRetailer);

    return copiedData.map((product) => {
        const foundItem = quantity.data.find((item: any) => {
            if(isRetailer) {
                return item.merchantSku?.toLowerCase() === product.sku.toLowerCase();
            } else {
                return item.supplierSku?.toLowerCase() === product.sku.toLowerCase() && item.supplierId === product.supplierId && item.merchantId === product.merchantId;
            }
        });

        product.quantity = foundItem?.quantity || 0;
        product.cost = foundItem?.cost || 0;
        product.status = foundItem?.status || 0;
        product.nextAvailableDate = foundItem?.nextAvailableDate || 0;
        return product;
    });
}
//endregion

const defaultConfig = {
    odata: true,
    selectable: true,
    order: 'asc',
    orderBy: 'name',
    pageSize: 25,
    pageSizes: [25, 50, 100],
    title: `poc_catalog`,
    refresh: false,
    term: "",
    facets: ["tags", "approvalStatus", "syncStatus"],
    filter: null,
    filterInUrl: true
}

const retailerConfig = {
    ...defaultConfig,
    savedSearchEnabled: true,
    tableType: 'poc-mycatalog',
    url: retailerCatalogSearch,
    onFormatData: async (data) => {
        return await getInventory(data, true);
    },
    columns: [
        {
            id: "id", label: "Id", filterable: false, hidden: true
        },
        {
            id: "name",
            label: (
                <div style={{position: "relative", display: "flex", alignItems: "center", justifyContent: "flex-start"}}>
                    <Chip
                        label="Parent SKU"
                        size="small"
                        variant="outlined"
                        style={{
                            fontSize: "14px",
                            fontWeight: 400,
                            position: "absolute",
                            top: -25,
                            left: 0,
                            zIndex: 1,
                            background: "#fff"
                        }}
                    />
                    <span style={{fontSize: "14px", fontWeight: 700, marginLeft: 0}}>Product Name | Variant Name</span>
                </div>
            ),
            cellStyle: {
                height: "6em"
            },
            hidden: false,
            toggleable: false,
            filterable: false,
            sortable: true,
            template: (row, column, colIndex, rowIndex, addFilter) => <ProductColumn row={row} column={column}
                                                                          colIndex={colIndex} rowIndex={rowIndex} addFilter={addFilter}/>
        },
        {
            id: "sku",
            label: "Variant SKU",
            hidden: false,
            toggleable: false,
            filterable: false,
            sortable: true,
            template: (row, column, colIndex, rowIndex) => <Typography style={{marginRight: "40px"}}>{row.sku}</Typography>
        },
        {
            id: "quantity", label: "Quantity",
            filterable: false,
            toggleable: true,
            sortable: false,
            customData: true,
            hidden: false,
            // Header cells get padding:0 when the table is selectable, so a header
            // whose column has no width sits flush against the next one. cellStyle is
            // spread into the HEADER cell only (CatalogManagerTable), so this adds the
            // gap without capping the body cell's content.
            cellStyle: {paddingRight: "24px"},
            template: (row, column, colIndex, rowIndex) => (
                <Typography style={{marginRight: "40px"}}>{row.quantity}</Typography>
            )
        },
        {
            id: "cost", label: "Cost",
            filterable: false,
            toggleable: true,
            sortable: false,
            customData: true,
            hidden: true,
            template: (row, column, colIndex, rowIndex) => (
                row.cost ? <Typography style={{marginRight: "40px"}}>{row.cost}</Typography> : null
            )
        },
        {
            id: "status", label: "Status",
            filterable: false,
            toggleable: true,
            sortable: false,
            customData: true,
            hidden: true,
            width: "8em",
            template: (row, column, colIndex, rowIndex) => {
                if (!row.status) return null;
                return (
                    <Typography style={{marginRight: "40px"}}>
                        {row.status.length > 8 ? `${row.status.substring(0, 8)}...` : row.status}
                    </Typography>
                );
            }
        },
        {
            id: "nextAvailableDate", label: "Next Available Date",
            filterable: false,
            toggleable: true,
            sortable: false,
            customData: true,
            hidden: true,
            width: "14em",
            template: (row, column, colIndex, rowIndex) => (
                <Typography style={{marginRight: "40px"}}>
                    {row.nextAvailableDate ? moment(row.nextAvailableDate).format('MM/DD/YYYY') : 'N/A'}
                </Typography>
            )
        },
        {
            id: "parentSku",
            label: "Parent SKU",
            hidden: true,
            toggleable: true,
            filterable: true,
            sortable: true,
            allowStringOperators: true,
            template: (row, column, colIndex, rowIndex, addFilter) => <ParentSkuColumn row={row} column={column}
                                                                            addFilter={addFilter}
                                                                            colIndex={colIndex} rowIndex={rowIndex}/>
        },
        {
            id: "category",
            label: "Category",
            hidden: false,
            toggleable: true,
            sortable: true,
            // Header-only padding (see quantity above). NOT a width: category paths
            // like "major_appliances_and_vacuums" are single unbreakable tokens, so a
            // fixed width clips them instead of wrapping.
            cellStyle: {paddingRight: "24px"},
            getOptions: () => getCategories(),
            template: (row, column, colIndex, rowIndex) => <CategoryColumn row={row} column={column}
                                                                           colIndex={colIndex} rowIndex={rowIndex}/>

        },
        {
            id: "tags",
            label: "Tags",
            hidden: false,
            toggleable: true,
            type: "array",
            // Rows with no tags render an empty cell, which collapses the column to
            // the "Tags" header and runs it into "Last Update". Header-only padding
            // fixes that without capping rows that do have chips.
            cellStyle: {paddingRight: "24px"},
            getOptions: () => getTags(true),
            template: (row, column, colIndex, rowIndex, addFilter) => <TagsColumn row={row} column={column}
                                                                                  addFilter={addFilter}
                                                                                  colIndex={colIndex}
                                                                                  rowIndex={rowIndex}
                                                                                  hoverTemplate={(row, column, colIndex, rowIndex, addFilter) => <TagsHoverTemplate tags={row.tags || []} addFilter={addFilter} />}/>
        },
        {
            id: "lastUpdate",
            label: "Last Update",
            type: "date",
            hidden: false,
            toggleable: true,
            sortable: true,
            template: (row, column, colIndex, rowIndex) => <LastUpdateColumn row={row} column={column}
                                                                             colIndex={colIndex}
                                                                             rowIndex={rowIndex}/>
        },
        {
            id: "created",
            label: "Created",
            type: "date",
            hidden: false,
            toggleable: true,
            sortable: true,
            template: (row, column, colIndex, rowIndex) => <CreatedDateColumn row={row} column={column}
                                                                             colIndex={colIndex}
                                                                             rowIndex={rowIndex}/>
        },
        {
            id: "supplierName",
            label: "Supplier Name",
            hidden: true,
            toggleable: true,
            sortable: false,
            // Header cells get padding:0 when the table is selectable
            // (CatalogManagerTable), so a column with no width collapses to its
            // text and the header runs into the next one ("Supplier NameSync
            // Status"). Same width convention as the other non-sortable columns
            // above (status 8em, nextAvailableDate 14em).
            width: "14em",
            defaultFilterOperator: "eq",
            getOptions: getCompanyNames,
            template: (row, column, colIndex, rowIndex) => <SupplierColumn row={row} column={column}
                                                                           colIndex={colIndex} rowIndex={rowIndex}/>
        },
        {
            id: "syncStatus",
            label: "Sync Status",
            hidden: false,
            toggleable: true,
            filterable: false,
            sortable: true,
            template: (row, column, colIndex, rowIndex, addFilter) => <ListingStatusColumn row={row} column={column}
                                                                                           colIndex={colIndex}
                                                                                           addFilter={addFilter}
                                                                                           rowIndex={rowIndex}/>
        },
        {
            id: "approvalStatus",
            label: "Approval Status",
            hidden: true,
            toggleable: true,
            filterable: false,
            sortable: true,
            template: (row, column, colIndex, rowIndex, addFilter) => <ApprovalStatusColumn row={row} column={column}
                                                                                            colIndex={colIndex}
                                                                                            addFilter={addFilter}
                                                                                            rowIndex={rowIndex}/>
        },
        {
            id: "sku", label: "SKU", hidden: true, filterable: false,
        },
        {
            id: "supplierId", label: "Supplier Id", hidden: true, filterable: true, type: 'number'
        },
        {
            id: "merchantId", label: "Merchant Id", hidden: true, filterable: false, type: 'number'
        },
        {
            id: "merchantName", label: "Merchant Name", hidden: true, filterable: false,
            template: (row, column, colIndex, rowIndex, addFilter) => <MerchantNameColumn row={row} column={column}
                                                                                            colIndex={colIndex}
                                                                                            addFilter={addFilter}
                                                                                            rowIndex={rowIndex}/>
        },
        {
            id: "image", label: "Image", filterable: false, hidden: true
        },
        {
            id: "description", label: "Description", hidden: true, filterable: false,
        },
        {
            id: "errorMessage",
            label: "Error Message",
            hidden: true,
            sortable: true,
            filterable: true,
            toggleable: true,
            template: (row, column, colIndex, rowIndex) => (
                <Typography>{row.errorMessage}</Typography>
            )
        },
        {
            id: "attributeSet",
            label: "Attribute Set",
            filterable: true,
            hidden: true,
            getOptions: () => getAttributeSets()
        },
        {
            id: "imageCount", label: "Image Count", filterable: false, hidden: true
        },
        {empty: true, hidden: true}, // Header for action col 1
        {empty: true, hidden: true} // Header for action col 2
    ],
}

const retailerConfigPartnerView = {
    ...retailerConfig,
    savedSearchEnabled: true,
    tableType: 'poc-partnercatalog',
    order: 'desc',
    orderBy: 'lastUpdate',
    url: supplierCatalogSearch,
    onFormatData: async (data) => {
        return await getInventory(data, false);
    },
    columns: [
        ...retailerConfig.columns.map((column) => {
            if (column.id === "approvalStatus") {
                return {
                    ...column,
                    hidden: false
                }
            } else if (column.id === "syncStatus") {
                return {
                    ...column,
                    hidden: true
                }
            } else if (column.id === "supplierName") {
                return {
                    ...column,
                    hidden: false,
                    // Keep Supplier Catalog's Supplier Name always-visible and out of its
                    // column picker. Pinned explicitly so it does NOT inherit toggleable:true
                    // from retailerConfig (My Catalog [LB-500]).
                    toggleable: false
                }
            }

            return column;
        })
    ]
}

const supplierConfig = {
    ...defaultConfig,
    savedSearchEnabled: true,
    tableType: 'poc-mycatalog',
    url: supplierCatalogSearch,
    onFormatData: async (data) => {
        return await getInventory(data, false);
    },
    columns: [
        {
            id: "id", label: "Id", filterable: false, hidden: true
        },
        {
            id: "name",
            label: (
                <div style={{position: "relative", display: "flex", alignItems: "center", justifyContent: "flex-start"}}>
                    <Chip
                        label="Parent SKU"
                        size="small"
                        variant="outlined"
                        style={{
                            fontSize: "14px",
                            fontWeight: 400,
                            position: "absolute",
                            top: -25,
                            left: 0,
                            zIndex: 1,
                            background: "#fff"
                        }}
                    />
                    <span style={{fontSize: "14px", fontWeight: 700, marginLeft: 0}}>Product Name | Variant Name</span>
                </div>
            ),
            cellStyle: {
                height: "6em"
            },
            hidden: false,
            toggleable: false,
            filterable: false,
            sortable: true,
            template: (row, column, colIndex, rowIndex, addFilter) => <ProductColumn row={row} column={column}
                                                                          colIndex={colIndex} rowIndex={rowIndex} addFilter={addFilter}/>
        },
        {
            id: "sku",
            label: "Variant SKU",
            hidden: false,
            toggleable: false,
            filterable: false,
            sortable: true,
            template: (row, column, colIndex, rowIndex) => <Typography style={{marginRight: "40px"}}>{row.sku}</Typography>
        },
        {
            id: "quantity", label: "Quantity",
            filterable: false,
            toggleable: true,
            sortable: false,
            customData: true,
            hidden: false,
            template: (row, column, colIndex, rowIndex) => (
                <Typography style={{marginRight: "40px"}}>{row.quantity}</Typography>
            )
        },
        {
            id: "cost", label: "Cost",
            filterable: false,
            toggleable: true,
            sortable: false,
            customData: true,
            hidden: true,
            template: (row, column, colIndex, rowIndex) => (
                row.cost ? <Typography style={{marginRight: "40px"}}>{row.cost}</Typography> : null
            )
        },
        {
            id: "status", label: "Status",
            filterable: false,
            toggleable: true,
            sortable: false,
            customData: true,
            hidden: true,
            width: "8em",
            template: (row, column, colIndex, rowIndex) => {
                if (!row.status) return null;
                return (
                    <Typography style={{marginRight: "40px"}}>
                        {row.status.length > 8 ? `${row.status.substring(0, 8)}...` : row.status}
                    </Typography>
                );
            }
        },
        {
            id: "nextAvailableDate", label: "Next Available Date",
            filterable: false,
            toggleable: true,
            sortable: false,
            customData: true,
            hidden: true,
            width: "14em",
            template: (row, column, colIndex, rowIndex) => (
                <Typography style={{marginRight: "40px"}}>
                    {row.nextAvailableDate ? moment(row.nextAvailableDate).format('MM/DD/YYYY') : 'N/A'}
                </Typography>
            )
        },
        {
            id: "merchantName", label: "Merchant Name", hidden: false,
            filterable: true,
            toggleable: true,
            sortable: true,
            defaultFilterOperator: "eq",
            // getOptions: getSuppliers, TODO: getRetailers
            template: (row, column, colIndex, rowIndex, addFilter) => <MerchantNameColumn row={row} column={column}
                                                                                          colIndex={colIndex}
                                                                                          addFilter={addFilter}
                                                                                          rowIndex={rowIndex}/>
        },
        {
            id: "parentSku",
            label: "Parent SKU",
            hidden: false,
            toggleable: false,
            filterable: true,
            sortable: true,
            allowStringOperators: true,
            template: (row, column, colIndex, rowIndex, addFilter) => <ParentSkuColumn row={row} column={column}
                                                                                       addFilter={addFilter}
                                                                                       colIndex={colIndex} rowIndex={rowIndex}/>
        },
        {
            id: "category",
            label: "Category",
            hidden: false,
            toggleable: true,
            sortable: true,
            getOptions: () => getCategories(),
            template: (row, column, colIndex, rowIndex) => <CategoryColumn row={row} column={column}
                                                                           colIndex={colIndex} rowIndex={rowIndex}/>

        },
        {
            id: "tags",
            label: "Tags",
            hidden: false,
            toggleable: true,
            type: "array",
            getOptions: () => getTags(false),
            template: (row, column, colIndex, rowIndex, addFilter) => <TagsColumn row={row} column={column}
                                                                                  addFilter={addFilter}
                                                                                  colIndex={colIndex}
                                                                                  rowIndex={rowIndex}/>
        },
        {
            id: "lastUpdate",
            label: "Last Update",
            type: "date",
            hidden: false,
            toggleable: true,
            sortable: true,
            template: (row, column, colIndex, rowIndex) => <LastUpdateColumn row={row} column={column}
                                                                             colIndex={colIndex}
                                                                             rowIndex={rowIndex}/>
        },
        {
            id: "created",
            label: "Created",
            type: "date",
            hidden: false,
            toggleable: true,
            sortable: true,
            template: (row, column, colIndex, rowIndex) => <CreatedDateColumn row={row} column={column}
                                                                              colIndex={colIndex}
                                                                              rowIndex={rowIndex}/>
        },
        {
            id: "supplierName",
            label: "Supplier Name",
            hidden: true,
        },
        {
            id: "syncStatus",
            label: "Sync Status",
            hidden: true,
            toggleable: false,
            filterable: false,
            sortable: true,
            template: (row, column, colIndex, rowIndex, addFilter) => <ListingStatusColumn row={row} column={column}
                                                                                           colIndex={colIndex}
                                                                                           addFilter={addFilter}
                                                                                           rowIndex={rowIndex}/>
        },
        {
            id: "approvalStatus",
            label: "Approval Status",
            hidden: false,
            toggleable: true,
            filterable: false,
            sortable: true,
            template: (row, column, colIndex, rowIndex, addFilter) => <ApprovalStatusColumn row={row} column={column}
                                                                                            colIndex={colIndex}
                                                                                            addFilter={addFilter}
                                                                                            rowIndex={rowIndex}/>
        },
        {
            id: "sku", label: "SKU", hidden: true, filterable: false,
        },
        {
            id: "supplierId", label: "Supplier Id", hidden: true, filterable: true, type: 'number'
        },
        {
            id: "merchantId", label: "Merchant Id", hidden: true, filterable: false, type: 'number'
        },
        {
            id: "image", label: "Image", filterable: false, hidden: true
        },
        {
            id: "description", label: "Description", hidden: true, filterable: false,
        },
        {
            id: "errorMessage",
            label: "Error Message",
            hidden: true,
            sortable: true,
            filterable: true,
            toggleable: true,
            template: (row, column, colIndex, rowIndex) => (
                <Typography>{row.errorMessage}</Typography>
            )
        },
        {
            id: "attributeSet",
            label: "Attribute Set",
            filterable: true,
            hidden: true,
            getOptions: () => getAttributeSets()
        },
        {
            id: "imageCount", label: "Image Count", filterable: false, hidden: true
        },
        {empty: true, hidden: true}, // Header for action col 1
        {empty: true, hidden: true} // Header for action col 2
    ],
}

const supplierConfigPartnerView = {
    ...supplierConfig,
    savedSearchEnabled: true,
    tableType: 'poc-partnercatalog',
    url: "",
    onFormatData: async (data) => {
        return await getInventory(data, true);
    },
    columns: [
        ...supplierConfig.columns.map((column) => {
            if (column.id === "approvalStatus") {
                return {
                    ...column,
                    hidden: true
                }
            } else if (column.id === "syncStatus") {
                return {
                    ...column,
                    hidden: false,
                    toggleable: true,
                }
            } else if (column.id === "supplierName") {
                return {
                    ...column,
                    hidden: true
                }
            } else if (column.id === "merchantName") {
                return {
                    ...column,
                    hidden: true
                }
            } else if (column.id === "tags") {
                return {
                    ...column,
                    hidden: true
                }
            }

            return column;
        })
    ]
}

export const defaultEventConfig = {
    url: Config.api + "/odata/Company/VwActivityEvents",
    savedSearchEnabled: true,
    columns: [
        {
            id: 'EventDate',
            type: 'date',
            filterable: true,
            sortable: true,
            label: 'Date',
            template: (row, column, colIndex, rowIndex) => (
                <Typography>{moment(row.EventDate).format('MM/DD/YYYY hh:mm A')}</Typography>
            ),
            width: '15em'
        },
        {id: 'PartnerCoId', label: "Partner CoId", hidden: true, filterable: true, sortable: true, width: '8em'},
        {id: 'PartnerCompanyName', filterable: true, sortable: true, label: 'Partner', width: '14em', getOptions: getCompanyNames},
        {id: 'EventLevel', label: 'Severity', width: '6em', filterable: true, sortable: true},
        {id: 'Summary', label: 'Summary', filterable: true, allowStringOperators: true},
        {id: 'Details', label: 'Details', filterable: true, allowStringOperators: true, width: '16em',
            hoverTemplate: (row, column) => row[column.id] || ''
        },
        {
            id: 'Viewed', label: "Viewed", sortable: true, template: (row, column, colIndex, rowIndex) => {
                return (
                    <CatalogCheckbox checked={row.Viewed} disabled={true}/>
                )
            }
        },
        {id: 'EventTypeId', hidden: true},
        {
            id: 'Id',
            width: '10em',
            stopPropagation: 'true',
            filterable: false,
            template: (row, column, colIndex, rowIndex, onAddFilter, setEventId, setEventViewed, setEventDialogOpen) =>
                <EventIdColumn
                    row={row} setEventId={setEventId} setEventViewed={setEventViewed}
                    setEventDialogOpen={setEventDialogOpen}/>
        },
        {id: 'CustomerViewable', hidden: true, filterable: false},
    ],
    order: 'desc',
    orderBy: 'Id',
    keyField: 'Id',
    pageSize: 25,
    pageSizes: [25, 50, 100],
    title: "Event Log",
    refresh: false,
    filterInUrl: false,
    filter: {
        filters: [
            {field: "CustomerViewable", operator: 'eq', value: true, system: true},
            {
                filters: [
                    {field: "EventTypeId", operator: 'eq', value: 70, system: true},
                    {field: "EventTypeId", operator: 'eq', value: 71, system: true}
                ], logic: 'or', system: true
            }
        ], logic: 'and'
    },
};

const defaultMessagesConfig = {
    url: Config.api + "/odata/Company/VwCompanyPosts",
    savedSearchEnabled: true,
    columns: [
        {id: 'Id', hidden: true},
        {id: 'RequireAck', hidden: true, label: "Action Required", filterable: true, type: 'bool'},
        {id: 'Acknowledged', hidden: true, label: "Acknowledged", filterable: true, type: 'bool'},
        {id: 'Sender', hidden: true},
        {id: 'LastReplyByPartner', hidden: true},
        {id: 'SenderName', filterable: true, label: 'From', width: '16em', getOptions: getCompanyNames},
        {
            id: 'Subject',
            filterable: true,
            allowStringOperators: true,
            label: 'Subject',
            template: (row, column, colIndex, rowIndex, addFilter) => {
                return (
                    <div>
                        <span
                            style={{
                                verticalAlign: 'middle',
                                fontWeight: row.LastReplyByPartner ? 'bold' : ''
                            }}>
                            {row.Subject}
                        </span>

                        {row.Sender !== Auth.getAccountNumber() && row.RequireAck && !row.Acknowledged ?
                            <Chip label="Action Required"
                                  style={{
                                      marginLeft: '0.5rem',
                                      backgroundColor: "#EE3224",
                                      color: 'white',
                                      fontWeight: 'bold',
                                      minWidth: '8em'
                                  }}/> : ''}


                        {!(!row.LastReplyByPartner) &&
                            <RepliedIcon style={{color: "#ee3224", fontSize: "10px", marginLeft: "0.5em"}}/>}
                    </div>
                )
            }
        },
        {
            id: "Reference", label: "SKU",
            template: (row, column, colIndex, rowIndex, addFilter, isRetailer) => <MessageSkuColumn row={row}
                                                                                                    column={column}
                                                                                                    colIndex={colIndex}
                                                                                                    addFilter={addFilter}
                                                                                                    rowIndex={rowIndex}
                                                                                                    isRetailer={isRetailer}/>,
        },
        {
            id: 'Status',
            filterable: true,
            sortable: true,
            label: 'Status',
            template: (row, column, colIndex, rowIndex, addFilter) => <MessageStatusColumn row={row} column={column}
                                                                                           colIndex={colIndex}
                                                                                           addFilter={addFilter}
                                                                                           rowIndex={rowIndex}/>,
            width: '15em'
        },
        {
            id: 'Date',
            type: 'date',
            filterable: true,
            sortable: true,
            label: 'Date',
            template: (row, column, colIndex, rowIndex, addFilter) => (
                <Moment format='MM/DD/YYYY hh:mm A'>{row.Date}</Moment>
            ),
            width: '15em'
        },
        {
            id: 'LastUpdated',
            type: 'date',
            filterable: true,
            sortable: true,
            label: 'Last Updated',
            template: (row, column, colIndex, rowIndex, addFilter) => {
                if (row.LastUpdated == null) return (<></>);
                return (
                    <Moment format='MM/DD/YYYY hh:mm A'>{row.LastUpdated}</Moment>
                )
            },
            width: '15em'
        },
        {
            id: 'Id',
            filterable: false,
            sortable: false,
            width: '20em',
            stopPropagation: 'true',
            template: (row, column, colIndex, rowIndex, onAddFilter, isRetailer) => <MessageIdColumn row={row}
                                                                                                     isRetailer={isRetailer}/>
        }
    ],
    order: 'desc',
    orderBy: 'Id',
    keyField: 'Id',
    pageSize: 25,
    pageSizes: [25, 50, 100],
    title: "Messages",
    refresh: false,
    filterInUrl: false,
    filter: {
        filters: [
            {field: "Category", operator: 'eq', value: "POC", system: true},
        ], logic: 'and'
    },
};

function Catalog(props: CatalogProps) {
    let urlCatalog = new Helpers().getUrlObject('catalog');
    const {setTitle} = props;

    const hasInventoryManagePermission = Auth.hasPermission("inventory/manage");

    const [currentCatalog, setCurrentCatalog] = React.useState(urlCatalog || "dashboard");
    const [myCatalogConfig, setMyCatalogConfig] = React.useState<CatalogConfigShape>(props.isRetailer ? {...retailerConfig} : {...supplierConfig});
    const [partnerCatalogConfig, setPartnerCatalogConfig] = React.useState<CatalogConfigShape>(props.isRetailer ? {...retailerConfigPartnerView} : {...supplierConfigPartnerView});
    const [eventsConfig, setEventsConfig] = React.useState<CatalogConfigShape>({...defaultEventConfig});
    const [messagesConfig, setMessagesConfig] = React.useState<CatalogConfigShape>({...defaultMessagesConfig});

    const [loading, setLoading] = React.useState(true);

    // Supplier Side Only //
    const [newProductRetailer, setNewProductRetailer] = React.useState<string | number>("");
    const [newProductRetailerSelectDialogOpen, setNewProductRetailerSelectDialogOpen] = React.useState(false);
    const [availablePartners, setAvailablePartners] = React.useState<any[]>([]);

    // configRef tracks whichever config is currently rendering (mycatalog vs
    // partnercatalog) so the column-mutation effect can patch the live shape.
    const configRef = React.useRef<CatalogConfigShape | null>(null);
    configRef.current = currentCatalog === "mycatalog" ? myCatalogConfig : partnerCatalogConfig;
    ////////////////////////

    // POC System Settings //
    const [forceUniqueRetailerSku, setForceUniqueRetailerSku] = React.useState(false);

    // Message Notifications //
    // This might change in the future, but for now we're just going
    // to count new messages here to add a badge to the Messages Tab
    const [newMessages, setNewMessages] = React.useState(0);
    const [newMessagesDialogOpen, setNewMessagesDialogOpen] = React.useState(false);
    ///////////////////////////

    // Refresh Modal State //
    const [refreshModalOpen, setRefreshModalOpen] = React.useState(false);
    ////////////////////////

    const updateCatalog = (catalog: string, clearFilters: boolean) => {
        if (!props.isRetailer && catalog === "partnercatalog") {
            let merchantId = new Helpers().getUrlObject('merchantId') || "";
            let searchUrl = merchantId === "" ? "" : retailerCatalogSearch + `?merchantId=${merchantId}`;
            setPartnerCatalogConfig({...partnerCatalogConfig, url: searchUrl});
        }

        // Build the new location object with or without filters
        let updatedLocation = { ...props.location };

        if (clearFilters) {
            // Clear filter parameters (q.*), column visibility (q_hide, q_show), saved search, and legacy params
            const params = new URLSearchParams(props.location.search);
            const keysToDelete: string[] = [];
            const keyIter = params.keys();
            let keyResult = keyIter.next();
            while (!keyResult.done) {
                const key = keyResult.value;
                // Clear filters, column settings, saved search, and any params starting with underscore (legacy/spurious)
                // The underscore params are often added by external systems and should be cleaned up
                if (key && (
                    key.startsWith('q.') ||
                    key === 'q_hide' ||
                    key === 'q_show' ||
                    key === 'savedSearch' ||
                    key.startsWith('_')
                )) {
                    keysToDelete.push(key);
                }
                keyResult = keyIter.next();
            }
            keysToDelete.forEach(key => params.delete(key));
            const newSearch = params.toString() ? `?${params.toString()}` : '';

            // Update the location object that will be passed to setUrlObject
            updatedLocation.search = newSearch;

            // Also update the actual browser URL
            props.history.replace({pathname: props.location.pathname, search: newSearch});

            // Clear the search term in all catalog configs
            setMyCatalogConfig(prev => ({...prev, term: ""}));
            setPartnerCatalogConfig(prev => ({...prev, term: ""}));
            setEventsConfig(prev => ({...prev, term: ""}));
            setMessagesConfig(prev => ({...prev, term: ""}));
        }

        // Pass the updated location (with filters cleared if needed) to setUrlObject
        new Helpers().setUrlObject('catalog', catalog, props.history, updatedLocation);
        setCurrentCatalog(catalog);
    }

    useEffect(() => {
        const updateTitle = async () => {
            if(setTitle) {
                const urlCatalog = (new Helpers() as any).getUrlObject('catalog', props.location);
                if (currentCatalog === 'dashboard' && !urlCatalog) {
                    return; // Skip - URL will be updated soon
                }

                switch(currentCatalog) {
                    case "dashboard":
                        setTitle('POC Dashboard', true);
                        break;
                    case "mycatalog":
                        setTitle('POC My Catalogs', true);
                        break;
                    case "partnercatalog":
                        const merchantId = (new Helpers() as any).getUrlObject('merchantId', props.location);
                        if (merchantId) {
                            try {
                                const partners = await utilGetPartners();
                                if (partners && !partners.errorMessage) {
                                    const partner = partners.find(p => p.Id === parseInt(merchantId));
                                    if (partner) {
                                        setTitle(`POC: ${partner.CompanyName}`, true);
                                        break;
                                    }
                                }
                            } catch (error) {
                                console.error('Error fetching partner name:', error);
                                // Error fetching partner name, use default title
                            }
                        }
                        // Default title if no merchantId or partner not found
                        setTitle(props.isRetailer ? 'POC Supplier Catalogs' : 'POC Retailer Catalogs', true);
                        break;
                    case "events":
                        setTitle('POC Events', true);
                        break;
                    case "messages":
                        setTitle('POC Messages', true);
                        break;
                    default:
                        setTitle('Product Onboarding Center');
                }
            }
        };

        updateTitle();
    }, [currentCatalog, setTitle, props.isRetailer, props.location]);

    useEffect(() => {
        const getNewMessages = async () => {
            const url = Config.api + "/odata/Company/VwCompanyPosts";
            const filter = `(Category eq 'POC' and Status eq 'New')`;

            try {
                let response = await axios.get(url, {
                    params: {
                        $count: true,
                        $top: 0,
                        $filter: filter,
                    }
                });

                setNewMessages(response.data["@odata.count"]);
            } catch (e) {

            }
        }

        const getPartners = async () => {
            let partners = await utilGetPartners();
            if (!partners.errorMessage) {
                setAvailablePartners(partners);
            } else {
                console.error("Error getting partners, please try again later.");
            }
        }

        const getPocSystemSettings = async () => {
            try {
                const settings = (await axios.get(Config.api + `/odata/Company/Functions.GetSystemSettings?system=ProductOnboardingCenter`)).data || {};
                setForceUniqueRetailerSku(settings.POCForceUniqueRetailerSku === true || settings.POCForceUniqueRetailerSku === 'true');
            } catch (e) {
                // non-critical, default to false
            }
        }

        const init = async () => {
            setLoading(true);
            if (urlCatalog === "" || urlCatalog == null) {
                new Helpers().setUrlObject('catalog', "dashboard", props.history, props.location);
                setCurrentCatalog("dashboard")
            } else {
                updateCatalog(urlCatalog, false);
            }
            await Promise.all([getNewMessages(), getPartners(), getPocSystemSettings()]);
            setLoading(false);
        }

        init();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Update column names for syndication suppliers //
        if(!props.isRetailer && currentCatalog === "mycatalog" && SUPPLIER_SYNDICATION_ENABLED) {
            const isMarketplace = props.retailers!.find((retailer: any) => retailer.Marketplace === true) !== undefined;
            const isDropship = props.retailers!.find((retailer: any) => retailer.Marketplace === false) !== undefined;
            const columnsCopy = [...configRef.current.columns];

            columnsCopy.forEach((column: any) => {
                if (column.id === "merchantName") {
                    column.label = (isMarketplace) ? "Channel" : "Merchant Name";
                }

                if (column.id === "approvalStatus") {
                    column.hidden = !isDropship;
                }

                if (column.id === "syncStatus") {
                    column.hidden = !isMarketplace;
                }
            });

            configRef.current.columns = columnsCopy;
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentCatalog]);

    return (
        <Grid container component={Root} style={{gap: pageContentGap, padding: "24px 14px 24px 14px"}}>
            <Grid container justifyContent={"space-between"} direction={"row"} alignItems={"center"} style={{width: "100%"}}>
                <Grid item>
                    <Header>
                        Product Onboarding Center
                    </Header>
                </Grid>

                <Grid item style={{display: "flex", gap: "8px"}}>
                    {props.isRetailer && ["mycatalog", "partnercatalog"].includes(currentCatalog) && (
                        <Button variant="outlined"
                                onClick={() => setRefreshModalOpen(true)}
                        >
                            <Sync style={{marginRight: "5px"}}/>
                            Refresh
                        </Button>
                    )}

                    {(props.isRetailer || (!props.isRetailer && SUPPLIER_SYNDICATION_ENABLED && props.retailers && props.retailers.length > 0)) && (
                        <Button variant="outlined"
                                component={Link}
                                to={
                                    props.isRetailer
                                        ? "/productonboardingcenter/settings"
                                        : "/productonboardingcenter/settings/channel-settings"
                                }
                        >
                            <Settings style={{marginRight: "5px"}}/>
                            Settings
                        </Button>
                    )}

                    {currentCatalog === "messages" && (
                        <Button variant="contained" color="primary"
                                startIcon={<AddCircleOutline/>}
                                onClick={() => {
                                    setNewMessagesDialogOpen(true);
                                }}>
                            New Message
                        </Button>
                    )}

                    {["mycatalog", "partnercatalog"].includes(currentCatalog) && (
                        <>
                            <Button
                                    startIcon={<ImportIcon style={{height: "18px", width: "18px"}}/>}
                                    component={Link}
                                    to={
                                        props.isRetailer && currentCatalog === "partnercatalog"
                                            ? "/productonboardingcenter/import/products?partnerId=00000"
                                            : props.isRetailer
                                                ? "/productonboardingcenter/import/products"
                                                : "/productonboardingcenter/import"
                                    }
                            >
                                Import
                            </Button>

                            <Button
                                    startIcon={<ExportIcon style={{height: "18px", width: "18px"}}/>}
                                    disabled={currentCatalog === "events"}
                                    className={`${(currentCatalog === "events" || !props.isRetailer) ? 'disabled-button' : ''}`}
                                    component={Link}
                                    to={currentCatalog === "partnercatalog" || !props.isRetailer
                                        ? "/productonboardingcenter/export/partnerProducts"
                                        : "/productonboardingcenter/export/products"}
                            >
                                Export
                            </Button>

                            <Button variant="contained" color="primary"
                                    disabled={currentCatalog !== "mycatalog" || !hasInventoryManagePermission}
                                    startIcon={<AddCircleOutline/>}
                                    sx={{ whiteSpace: "nowrap" }}
                                    component={props.isRetailer ? Link : undefined}
                                    to={props.isRetailer
                                        ? `/productonboardingcenter/product?catalog=${new Helpers().getUrlObject("catalog")}`
                                        : undefined}
                                    onClick={() => {
                                        if (!props.isRetailer)
                                            setNewProductRetailerSelectDialogOpen(true);
                                    }}
                            >
                                Add new Item
                            </Button>
                        </>
                    )}
                </Grid>
            </Grid>

            <Grid container wrap="wrap" style={{gap: pageContentGap}}>
                <Paper>
                    <CatalogTab title={"Dashboard"}
                                description={"View your product performance and analytics"}
                                selected={currentCatalog === "dashboard"}
                                onClick={() => updateCatalog("dashboard", true)}
                    />
                </Paper>

                <Paper>
                    <CatalogTab title={props.isRetailer ? "Supplier Catalogs" : "Retailer Catalogs"}
                                description={props.isRetailer ? "View and manage Supplier Catalogs" : "View retailer catalogs"}
                                selected={currentCatalog === "partnercatalog"}
                                onClick={() => updateCatalog("partnercatalog", true)}
                    />
                </Paper>

                <Paper>
                    <CatalogTab title={"My Catalog"}
                                description={"View and manage all your listings as you need"}
                                selected={currentCatalog === "mycatalog"}
                                onClick={() => updateCatalog("mycatalog", true)}
                    />
                </Paper>

                <Paper>
                    <CatalogTab title={"Events"} description={"View all events for your catalog"}
                                selected={currentCatalog === "events"}
                                onClick={() => updateCatalog("events", true)}
                    />
                </Paper>

                <Paper sx={{position: "relative"}}>
                    {newMessages > 0 && (
                        <span style={{
                            position: "absolute",
                            right: "10px",
                            top: "10px",
                            width: "20px",
                            height: "20px",
                            background: "rgb(238, 50, 36)",
                            borderRadius: "100000px",
                            // display: "flex",
                            // justifyContent: "center",
                            // alignItems: "center",
                            textAlign: "center",
                            color: "white",
                            fontSize: "14px",
                        }}>
                        {newMessages}
                    </span>
                    )}
                    <CatalogTab title={"Messages"} description={"View all messages for your catalog"}
                                selected={currentCatalog === "messages"}
                                onClick={() => updateCatalog("messages", true)}
                    />
                </Paper>
            </Grid>

            {loading ? (
                <Grid container style={{gap: pageContentGap, flexDirection: "column"}}>
                    <Grid container style={{gap: pageContentGap, flexWrap: "wrap"}}>
                        <ContentLoader preserveAspectRatio='none' style={{height: '80px', minWidth: "228px", flex: 1, borderRadius: "6px"}} primaryColor='#e1e1e1' secondaryColor='#d8d8d8'/>
                        <ContentLoader preserveAspectRatio='none' style={{height: '80px', minWidth: "228px", flex: 1, borderRadius: "6px"}} primaryColor='#e1e1e1' secondaryColor='#d8d8d8'/>
                        <ContentLoader preserveAspectRatio='none' style={{height: '80px', minWidth: "228px", flex: 1, borderRadius: "6px"}} primaryColor='#e1e1e1' secondaryColor='#d8d8d8'/>
                        <ContentLoader preserveAspectRatio='none' style={{height: '80px', minWidth: "228px", flex: 1, borderRadius: "6px"}} primaryColor='#e1e1e1' secondaryColor='#d8d8d8'/>
                    </Grid>

                    <Grid container style={{gap: pageContentGap}}>
                        <ContentLoader preserveAspectRatio='none' style={{height: '200px', flex: 1, borderRadius: "6px"}} primaryColor='#e1e1e1' secondaryColor='#d8d8d8'/>
                        <ContentLoader preserveAspectRatio='none' style={{height: '200px', flex: 1, borderRadius: "6px"}} primaryColor='#e1e1e1' secondaryColor='#d8d8d8'/>
                    </Grid>

                    <Grid container>
                        <ContentLoader preserveAspectRatio='none' style={{height: '300px', width: '100%', borderRadius: "6px"}} primaryColor='#e1e1e1' secondaryColor='#d8d8d8'/>
                    </Grid>
                </Grid>
            ) : (
                <>
                    {currentCatalog === "dashboard" && (
                        <Dashboard history={props.history} location={props.location} isRetailer={props.isRetailer}
                                   retailers={props.retailers} availableRetailers={availablePartners}
                                   currentCatalog={currentCatalog} setCurrentCatalog={setCurrentCatalog}/>
                    )}

                    {/*Strange state caching occurs when we use a ? : conditional operator to render the table.*/}
                    {/*So we have to use this method instead, which is a bit more verbose but works.*/}
                    {currentCatalog === "events" && (
                        <EnhancedTable config={eventsConfig} history={props.history} location={props.location}
                                       template={(table) =>
                                           <CatalogEventResults history={props.history} location={props.location}
                                                                currentCatalog={currentCatalog}
                                                                table={table} config={eventsConfig}
                                                                setConfig={setEventsConfig}
                                                                condensed={false}
                                                                showSavedSearch={true}
                                                                savedSearchTableType="catalog-events"
                                           />
                                       }/>
                    )}

                    {currentCatalog === "messages" && (
                        <EnhancedTable config={messagesConfig} history={props.history} location={props.location}
                                       template={(table) =>
                                           <MessageResults
                                               isRetailer={props.isRetailer}
                                               history={props.history} location={props.location}
                                               currentCatalog={currentCatalog}
                                               table={table} config={messagesConfig}
                                               setConfig={setMessagesConfig}
                                               condensed={false}
                                           />
                                       }/>
                    )}

                    {currentCatalog === "mycatalog" && (
                        <EnhancedTable config={myCatalogConfig} history={props.history} location={props.location}
                                       template={(table) => (
                                           <CatalogResults
                                               retailers={props.retailers}
                               isRetailer={props.isRetailer}
                               history={props.history} location={props.location}
                               currentCatalog={currentCatalog}
                               table={table} config={myCatalogConfig} setConfig={setMyCatalogConfig}
                               forceUniqueRetailerSku={forceUniqueRetailerSku}
                           />
                       )}/>
                    )}

                    {currentCatalog === "partnercatalog" && (
                        <EnhancedTable config={partnerCatalogConfig} history={props.history} location={props.location}
                                       template={(table) => (
                                           <CatalogResults
                                               retailers={props.retailers}
                                               isRetailer={props.isRetailer}
                                               history={props.history} location={props.location}
                                               currentCatalog={currentCatalog}
                                               table={table} config={partnerCatalogConfig} setConfig={setPartnerCatalogConfig}
                                               forceUniqueRetailerSku={forceUniqueRetailerSku}
                                           />
                                       )}/>
                    )}
                    </>
            )}
            {/*New Message Dialog*/}
            <NewMessage
                open={newMessagesDialogOpen}
                onClose={() => setNewMessagesDialogOpen(false)}
                posts={[]}
                onSent={async () => {
                    setNewMessagesDialogOpen(false);
                    await new Promise(r => setTimeout(r, 750));
                    setMessagesConfig((prevState) => ({...messagesConfig, refresh: !prevState.refresh}));
                }}
                coid={Auth.getAccountNumber()} noref
            />
            {/*New Product Retailer Select (Supplier Side)*/}
            <Dialog open={newProductRetailerSelectDialogOpen}
                    onClose={() => setNewProductRetailerSelectDialogOpen(false)}
                    maxWidth={"sm"} fullWidth={true}
                    slotProps={{
                        paper: {
                            style: {
                                borderRadius: "12px",
                            }
                        }
                    }}
            >
                <DialogTitle>
                    <Grid container justifyContent={"space-between"} alignItems={"center"}>
                        <Grid item container style={{flex: 0.8, flexDirection: "column"}}>
                            <DialogHeader>
                                Retailer Select
                            </DialogHeader>
                            <Typography style={{fontSize: "12px", fontWeight: 300}}>
                                For which retailer would you like to create a new product for?
                            </Typography>
                        </Grid>

                        <IconButton onClick={() => setNewProductRetailerSelectDialogOpen(false)} size="large">
                            <HighlightOff style={{color: "red"}}/>
                        </IconButton>
                    </Grid>
                </DialogTitle>

                <DialogContent style={{padding: "0 24px 24px 24px"}}>
                    <Grid container>
                        <EnhancedSelect
                            label={"Retailer"}
                            options={availablePartners.map(retailer => ({
                                label: retailer.CompanyName, value: retailer.Id
                            }))}
                            value={{
                                label: availablePartners.find(retailer => retailer.Id === newProductRetailer)?.CompanyName || newProductRetailer,
                                value: newProductRetailer
                            }}
                            onChange={(e) => setNewProductRetailer(e.value)}
                            style={{width: "100%"}}
                        />
                    </Grid>
                </DialogContent>

                <DialogActions
                    style={{
                        padding: "12px 24px 24px 24px",
                    }}>
                    <Grid item container justifyContent={"flex-end"} style={{gap: "8px"}}>
                        <Button variant="outlined"
                                onClick={() => setNewProductRetailerSelectDialogOpen(false)}>
                            Cancel
                        </Button>

                        <Button variant="contained" color="primary"
                            disabled={newProductRetailer === ""}
                            onClick={() => {
                                props.history.push("/productonboardingcenter/product?catalog=mycatalog&partnerId=" + newProductRetailer);
                            }}
                        >
                            Continue
                        </Button>
                    </Grid>
                </DialogActions>
            </Dialog>
            {/*Refresh Supplier Data Modal*/}
            <RefreshSupplierDataModal
                open={refreshModalOpen}
                onClose={() => setRefreshModalOpen(false)}
                availableSuppliers={availablePartners}
                onSuccess={() => {
                    setRefreshModalOpen(false);
                    if (currentCatalog === "mycatalog") {
                        setMyCatalogConfig((prevState) => ({...prevState, refresh: !prevState.refresh}));
                    } else if (currentCatalog === "partnercatalog") {
                        setPartnerCatalogConfig((prevState) => ({...prevState, refresh: !prevState.refresh}));
                    }
                }}
            />
        </Grid>
    );
}

export default Catalog;
